import S, { DataSignal } from 's-js';
import * as Promise from 'bluebird';
import { tzmoment } from 'lib/tzmoment';
import { Entrypoints } from 'core/app/entrypoints';
import { IAjax, Client, Request } from 'lib/Ajax';
import TZDate from 'lib/TZDate';
import { PCCache } from 'data/PCCache';
import { PersonProfilePageCache } from './cache';
import { AutoSavingDraftObject } from 'lib/autoSave';
import { ValidationWatcher } from 'lib/validationWatcher';
import uniqueIndex, { UniqueIndex } from 'lib/uniqueIndex';
import { CaseInsensitiveContains } from 'lib/diacriticals';
import { DomainSummary } from 'data/models/DomainSummary';
import { UserSummary } from 'data/models/UserSummary';
import { EventType } from 'data/models/EventType';
import { PortCallChangeType } from 'server/enums/PortCallChangeType';
import { Event } from 'data/models/Event';
import { LandingPage } from 'data/models/LandingPage';
import { Notification } from 'data/models/Notification';
import { PCUser } from 'data/models/PCUser';
import { PCUserPortCallEditor, PCUserPortCallEditorData } from 'data/models/PCUserPortCallEditor';
import { PersonProfilePageResponse, PersonProfilePageResponseData, PersonProfilePageResponseRef, PersonProfilePageResponseRefData } from 'data/models/PersonProfilePageResponse';
import { PhoneNumber, PhoneNumberData } from 'data/models/PhoneNumber';
import { PortCallChange } from 'data/models/PortCallChange';
import { Role } from 'data/models/Role';
import { User } from 'data/models/User';
import { SendPasswordResetRequestData } from 'data/models/Accounts';
import { SelectProfileImage, SelectProfileImageParams } from 'portcall/selectprofileimage/model';
import { ChangePassword } from 'core/changepassword/model';
import { PhoneNumber as PhoneNumberViewModel } from 'portcall/phonenumber/model';
import { PersonProfilePopup, PersonProfilePopupParams } from 'portcall/personprofilepopup/model';
import { Typeahead } from 'portcall/typeahead/model';
import { UserSelector } from 'portcall/userselector/model';

export class PersonProfilePageParams {
    constructor(
        public id : number = 0
    ) { }
}

export interface PCUserViewModel {
    Model : PCUser;
    Validation : ValidationWatcher;
    User : () => UserViewModel | null;
    ShowSelectProfileImage : DataSignal<boolean>;
    ImageSelector : () => SelectProfileImage | null;
    PhoneNumbers : UserPhoneNumbersViewModel;
    PortCallEditors : PortCallEditorsViewModel;
    ShowSendPasswordReset : DataSignal<boolean>;
    SendPasswordResetModal : () => SendPasswordResetViewModel | null;
    ShowDeleteAccount : DataSignal<boolean>;
    DeleteAccountModal : () => DeleteAccountViewModel | null;
    ShowNotificationSettings : DataSignal<boolean>;
    NotificationSettingsModal : () => NotificationSettingsViewModel | null;
}

export interface UserViewModel {
    Model : User;
    Validation : ValidationWatcher;
}

export interface RoleViewModel {
    Model : Role;
    ShowAll : DataSignal<boolean>;
    Description : () => string;
}

export interface UserPhoneNumbersViewModel {
    Numbers : () => PhoneNumberViewModel[];
    Add: () => void;
}

export interface NotificationsViewModel {
    Notifications : () => NotificationViewModel[];
    UnviewedNotificationsCount : () => number;
}

export interface NotificationViewModel {
    Model : Notification;
    Event : () => EventViewModel | null;
}

export interface EventViewModel {
    Model : Event;
    Date : () => string;
    PortCallChange : () => PortCallChangeViewModel | null;
}

export interface PortCallChangeViewModel {
    Model : PortCallChange;
    FirstLineDate : () => string | null;
    FirstLineTime : () => string | null;
    ActualDate : () => string | null;
}

export interface PortCallEditorsViewModel {
    Editing : DataSignal<boolean>;
    Editors : () => PortCallEditorViewModel[];
    NewEditorSelector : Typeahead<PCUser>;
}

export interface PortCallEditorViewModel {
    Editor : () => PCUser | null;
    ToggleProfile : () => void;
    Remove : () => void;
    ProfilePopup : () => PersonProfilePopup | null;
}

export interface SendPasswordResetViewModel {
    Request : Request<{}>;
}

export interface DeleteAccountViewModel {
    Action : () => Request<{}> | null;
    ActionSuccess : () => boolean;
    Delete : () => void;
    DoneDeleting : () => void;
}

export interface NotificationSettingsViewModel {
    NotificationEmail : (val? : string) => string | null;
}

export function PersonProfilePage(
    entrypoints : Entrypoints,
    ajax : IAjax,
    cache : PCCache,
    queryCache : PersonProfilePageCache,
    selectProfileImageModel : (params : SelectProfileImageParams) => SelectProfileImage,
    personProfilePopupModel : (params : PersonProfilePopupParams) => PersonProfilePopup,
    phoneNumberModel : (pn : PhoneNumber, deleteFn : (ts : Date) => void) => PhoneNumberViewModel,
    params : PersonProfilePageParams
) {
    const model = queryCache.PersonProfilePageResponses.update1(null, { UserId: params.id }),
        url = entrypoints.PersonProfilePage({ Id: params.id }),
        request = ajax.Url<PersonProfilePageResponseData>(url).Get(),
        editing = S.data(false),
        userContainer = S(() => model.PCUser() && PCUserViewModel(model.PCUser()!)),
        showChangePassword = S.data(false),
        changePassword = S(() => showChangePassword() ? ChangePassword({ ajax, entrypoints, userId: params.id, onHidePopup: () => showChangePassword(false) }) : null),
        showActionsMenu = S.data(false);

    request.done(data => {
        S.freeze(() => {
            queryCache.PersonProfilePageResponses.update1(request.Date, data);
            if (data.LandingPages) model.LandingPages(data.LandingPages.map(lp => new LandingPage(lp)));
        });

        // Post to ViewNotifications entrypoint to mark all unviewed notifications as viewed
        // NOTE: we do NOT update the UI after this call. The idea is that these notifications
        // will now appear as viewed the next time the user navigates to this page.
        if (model.CanMarkNotificationsAsViewed()) {
            const viewNotificationsUrl = entrypoints.ViewNotifications.replace('{id}', '' + params.id);
            ajax.Url(viewNotificationsUrl).Post({});
        }
    });

    return {
        Model: model,
        User: userContainer,
        Request: request,
        ChangePassword: changePassword,
        ShowChangePassword: showChangePassword,
        Editing: editing,
        Roles: model.Roles.map(RoleViewModel),
        Notifications: NotificationsViewModel(),
        UIThemes: () => Array.from(cache.UIThemes.items()),
        ShowActionsMenu: showActionsMenu,
        ToggleActionsMenu,
        GoBack: function () {
            history.back();
        }
    };

    function ToggleActionsMenu() {
        showActionsMenu(!showActionsMenu());
    }

    function MenuAction(action : () => void) {
        return () => S.freeze(() => {
            action();
            showActionsMenu(false);
        });
    }

    function RoleViewModel(role : Role) : RoleViewModel {
        const DEFAULT_CHARS = 20,
            showAll = S.data(false);

        return {
            Model: role,
            ShowAll: showAll,
            Description: () => {
                const descr = role.Description() || '';
                return showAll() ? descr : descr.substr(0, DEFAULT_CHARS) + '...';
            }
        };
    }

    function PCUserViewModel(user : PCUser) : PCUserViewModel {
        const pcUserClient = ajax.Url<PCUser>(() => user.Url() || ''),
            pcUserDraft = AutoSavingDraftObject(user, pcUserClient),
            pcUserValidation = ValidationWatcher(pcUserClient),
            _user = S(() => user.User() && UserViewModel(user.User()!)),
            showSelectProfileImage = S.data(false),
            imageSelector = S(() => showSelectProfileImage() ? ConstructSelectProfileImage() : null),
            showSendPasswordReset = S.data(false),
            sendPasswordResetModal = S(() => showSendPasswordReset() ? SendPasswordResetViewModel(user) : null),
            showDeleteAccount = S.data(false),
            deleteAccountModal = S(() => showDeleteAccount() ? DeleteAccountViewModel(user, () => showDeleteAccount(false)) : null),
            showNotificationSettings = S.data(false),
            notificationSettingsModal = S(() => showNotificationSettings() ? NotificationSettingsViewModel(pcUserDraft, () => _user() && _user()!.Model) : null),
            phoneNumbersVM = UserPhoneNumbersViewModel(user),
            portcallEditorsVM = PortCallEditorsViewModel(user);
        
        return {
            Model : pcUserDraft,
            Validation : pcUserValidation,
            User: _user,
            ImageSelector: imageSelector,
            ShowSelectProfileImage: showSelectProfileImage,
            PhoneNumbers: phoneNumbersVM,
            PortCallEditors: portcallEditorsVM,
            SendPasswordResetModal: sendPasswordResetModal,
            ShowSendPasswordReset: showSendPasswordReset,
            DeleteAccountModal: deleteAccountModal,
            ShowDeleteAccount: showDeleteAccount,
            NotificationSettingsModal: notificationSettingsModal,
            ShowNotificationSettings: showNotificationSettings
        };

        function ConstructSelectProfileImage() {
            editing(false);
            return selectProfileImageModel({
                image: user.Image,
                allowedImageUploadExtensions: model.Ref.AllowedImageUploadExtensions,
                maximumImageUploadFileSize: model.Ref.MaximumImageUploadFileSize,
                defaultImages: model.Ref.DefaultImages,
                minimumImageUploadImageSize: model.Ref.MinimumImageUploadImageSize,
                saveCroppedUploadUrl: entrypoints.SavePCUserImageUpload({ Id: params.id }),
                saveImageSelection: function () {
                    ajax.Url(() => user.Url() || '').Put(user);
                },
                onHide: () => showSelectProfileImage(false)
            });
        }
    }

    function UserViewModel(u : User) : UserViewModel {
        const client = ajax.Url<User>(() => u.Url() || ''),
            draft = AutoSavingDraftObject(u, client),
            validation = ValidationWatcher(client);
        
        return {
            Model: draft,
            Validation: validation
        };
    }

    function UserPhoneNumbersViewModel(user : PCUser) : UserPhoneNumbersViewModel {
        const client = ajax.Url<PhoneNumber, PhoneNumberData>(() => user.PhoneNumbersUrl() || '');

        return {
            Numbers: user.PhoneNumbers.map(pn => phoneNumberModel(pn, ts => RemoveFromCache(pn, ts))),
            Add: Add
        };

        function Add() {
            let req = client.Post(new PhoneNumber())
            req.done(function (data : PhoneNumberData) {
                cache.PhoneNumbers.update1(req.Date, data);
            });
        }

        function RemoveFromCache(pn : PhoneNumber, ts : Date) {
            const id = S.sample(() => PhoneNumber.key(pn));
            cache.PhoneNumbers.update(ts, [], [id]);
        }
    }

    function PortCallEditorsViewModel(user : PCUser) : PortCallEditorsViewModel {
        const editors = user.PortCallEditors
                .filter(editor => !editor.PortCallEditor() || editor.PortCallEditor()!.Active())
                .map(editor => PortCallEditorViewModel(user, editor)),
            newEditorSelector = NewEditorUserSelector(user);

        return {
            Editing: S.data(false),
            Editors: editors,
            NewEditorSelector: newEditorSelector
        };
    }

    function PortCallEditorViewModel(user : PCUser, editor : PCUserPortCallEditor) : PortCallEditorViewModel {
        const showProfile = S.data(false),
            profilePopup = S(() => showProfile() ? personProfilePopupModel({ id: editor.PortCallEditorId(), onHidePopup: () => showProfile(false) }) : null);

        return {
            Editor: () => editor.PortCallEditor(),
            ToggleProfile: () => showProfile(!showProfile()),
            Remove: Remove,
            ProfilePopup: profilePopup
        };

        function Remove() {
            let req = ajax.Url(() => editor.Url() || '').Delete();
            req.then(function () {
                const id = S.sample(() => PCUserPortCallEditor.key(editor));
                cache.PCUserPortCallEditors.update(req.Date, [], [id]);
            });
        }
    }

    function NewEditorUserSelector(user : PCUser) {
        const selectedUserId = S.value(0),
            editorsIdx = uniqueIndex(user.PortCallEditors, editor => editor.PortCallEditorId()),
            invitableEditors = model.InvitablePortCallEditors.filter(editor => !editorsIdx(editor.Id())()),
            userSelector = UserSelector({
                Users: invitableEditors,
                UserId: selectedUserId
            });

        let lastRequest = Promise.resolve();

        S(() => {
            if (selectedUserId()) {
                const url = (S.sample(user.PortCallEditorsUrlBase) || '').replace('{rightId}', '' + selectedUserId()),
                    client = ajax.Url<{}, PCUserPortCallEditorData>(url);

                lastRequest.finally(() => {
                    let req = client.Put({});
                    lastRequest = req.then(json => {
                        cache.PCUserPortCallEditors.update1(req.Date, json);
                    });
                });

                // Clear typeahead value
                userSelector.data('');
            }
        });

        return userSelector;
    }

    function DeleteAccountViewModel(user : PCUser, closeDeleteAccountModal : () => void) : DeleteAccountViewModel {
        const client = ajax.Url<PCUser, {}>(() => user.Url() || ''),
            action = S.data<Request<{}> | null>(null),
            actionSuccess = () =>
                !!action() && !!action()!.Response() && action()!.Response()!.Success;

        return {
            Action: action,
            ActionSuccess: actionSuccess,
            Delete: Delete,
            DoneDeleting: DoneDeleting
        };

        function Delete() {
            user.Active(false);
            action(client.Put(user));
        }

        function DoneDeleting() {
            closeDeleteAccountModal();
            // need to use a timeout otherwise modal hide animation doesn't finish
            // and semi-transparent modal backdrop is stuck on screen
            setTimeout(function () {
                window.history.back();
            }, 200);
        }
    }

    function SendPasswordResetViewModel(user : PCUser) : SendPasswordResetViewModel {
        const request = ajax.Url<SendPasswordResetRequestData>(entrypoints.SendPasswordReset).Post({
                UserId: user.Id()
            });

        return {
            Request: request
        };
    }

    function NotificationsViewModel() : NotificationsViewModel {
        const unviewedNotificationsCount = S(() =>
            model.Notifications().filter(notification => notification.WebDate() === null).length);

        return {
            Notifications: model.Notifications.map(NotificationViewModel),
            UnviewedNotificationsCount: unviewedNotificationsCount,
        };
    }

    function NotificationViewModel(notification : Notification) : NotificationViewModel {
        const event = S(() => notification.Event() && EventViewModel(notification.Event()!));
        return {
            Model : notification,
            Event : event
        };
    }

    function EventViewModel(event : Event) : EventViewModel {
        const pcChange = S(() => {
                if (event.Type() === EventType.PortCallChange) return event.PortCallChange() && PortCallChangeViewModel(event.PortCallChange()!);
                return null;
            }),
            date = S(() => tzmoment(event.Date()).format('MM/DD/YY HH:mm'));

        return {
            Model : event,
            Date: date,
            PortCallChange: pcChange
        };
    }

    function PortCallChangeViewModel(pcChange : PortCallChange) : PortCallChangeViewModel {
         const firstLine = S(() => {
                if (pcChange.Type() === PortCallChangeType.Creation) return tzmoment(pcChange.FirstLine());
                return null;
            }),
            actualDate = S(() => {
                if (pcChange.Type() === PortCallChangeType.LineTimeActual) return tzmoment(pcChange.ActualTime()).format('MM/DD');
                return null;
            });

        return {
            Model: pcChange,
            FirstLineDate: () => firstLine() && firstLine()!.format('MM/DD/YY'),
            FirstLineTime: () => firstLine() && firstLine()!.format('HH:mm'),
            ActualDate: actualDate
        };
    }

    function NotificationSettingsViewModel(user : PCUser, _u : () => User | null) : NotificationSettingsViewModel {
        const allowBlank = S.data(false),
            notificationEmail = function (val? : string) : string | null {
                if (arguments.length === 0) {
                    const notifEmail = user.NotificationEmail();
                    // Use account email as a default value, but also allow user to clear the value
                    // for ease of editing
                    if (allowBlank()) return notifEmail;
                    return notifEmail || _u() && _u()!.Email();
                }
                // Allow blank values
                if (!val) allowBlank(true);
                else allowBlank(false);
                // Blank value or value equal to account email -> set NotificaitonEmail to null so that defaults to account email
                const newVal = !val || val === S.sample(() => _u() && _u()!.Email()) ? null : val;
                user.NotificationEmail(newVal);
                return newVal;
            };

        return {
            NotificationEmail: notificationEmail,
        };
    }
}
export type PersonProfilePage = ReturnType<typeof PersonProfilePage>;


// WEBPACK FOOTER //
// ./apps/portcall/personprofilepage/model.ts